import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import GetAppIcon from '@mui/icons-material/GetApp';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import ReplayIcon from '@mui/icons-material/Replay';
import { Box } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import { TablePaginationActionsProps } from '@mui/material/TablePagination/TablePaginationActions';
import { GridSortModel } from '@mui/x-data-grid';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation } from 'react-query';
import {
  useRecoilStateLoadable,
  useRecoilValue,
  useRecoilValueLoadable,
  useSetRecoilState,
} from 'recoil';
import type { Stripe } from 'stripe';
import { endingDate } from '../../recoil/atoms/endingDate';
import { startingDate } from '../../recoil/atoms/startingDate';
import { IBusinessModel, Role, user } from '../../recoil/atoms/user';
import {
  methodNameEnum,
  refetchToggle,
} from '../../recoil/selectors/makePHPRequest';
import {
  IPermissions,
  permissionsSelector,
} from '../../recoil/selectors/permissionsSelector';
import { getApiMutation } from '../../services/rest/getApiMutation';
import {
  endpointEnum,
  makeAPIRequest,
  makeApiRequestCached,
} from '../../services/rest/makeAPIRequest';
import { EditBillingEmailModal } from '../components/modals/EditBillingEmailModal';
import { RefundProratedChargeModal } from '../components/modals/RefundProratedChargeModal';
import dataTableScss from '../modules/Manage/pages/queues-table/data-table.module.css';
import {
  DataTable,
  IColDef,
  IToolbarComponent,
} from '../modules/Manage/pages/queues-table/DataTable';
import { EnhancedTableToolbar } from '../modules/Manage/pages/queues-table/EnhancedTableToolbar';
export const Invoices = () => {
  const permissionsLoadable = useRecoilValueLoadable(permissionsSelector);
  const { role, callRecord } = permissionsLoadable.contents as IPermissions;
  type paginationDirection = 'forwards' | 'backwards';
  interface Cursor {
    // eslint-disable-next-line camelcase
    starting_after?: string;
    // eslint-disable-next-line camelcase
    ending_before?: string;
  }
  interface StripeStateData {
    direction: paginationDirection;
    page: number;
    hasChangedDirections: boolean;
    cursor: Cursor;
  }

  const [stripeState, setStripeState] = useState<StripeStateData>({
    direction: 'forwards',
    page: 1,
    hasChangedDirections: false,
    cursor: {},
  });

  const [pageSize, setPageSize] = useState<number>(10);
  const [showEditEmailModal, setShowEditEmailModal] = useState(false);
  const [
    showRefundProratedChargeModal,
    setShowRefundProratedChargeModal,
  ] = useState(false);
  const [refundProratedChargeId, setRefundProratedChargeId] = useState('');
  const [
    refundProratedChargeInvoiceId,
    setRefundProratedChargeInvoiceId,
  ] = useState('');
  const [refundProratedChargeAmount, setRefundProratedChargeAmount] = useState(
    0
  );
  const [sortModel] = useState<GridSortModel>([
    { field: 'created', sort: 'desc' },
  ]);
  const [userAtomLoadable, setUserAtom] = useRecoilStateLoadable(user);
  const userAtom = userAtomLoadable.contents;
  const thisBID = userAtom.businessid;
  const mutation = useMutation(getApiMutation(userAtom, setUserAtom));

  const startFromCalenderInUnix = useRecoilValue(startingDate).unix().valueOf();
  const endFromCalenderInUnix = useRecoilValue(endingDate).unix().valueOf();

  // const urlSearchParams = new URLSearchParams(window.location.search);
  const generateCreatedObj = () => {
    // const isDatePicked = urlSearchParams.get('start');

    // if (isDatePicked) {
    return {
      gte: startFromCalenderInUnix,
      lte: endFromCalenderInUnix,
    };
    // } else return {};
  };

  const resetStripe = () => {
    setStripeState({
      direction: 'forwards',
      page: 1,
      hasChangedDirections: false,
      cursor: {},
    });
  };

  useEffect(() => {
    resetStripe();
  }, [startFromCalenderInUnix, endFromCalenderInUnix]);

  const newCursor = useRef({});

  const billingEmailLoadable = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: 'legacy/getStripeBillingEmail',
      payloadJsonString: JSON.stringify({
        call: 'getStripeBillingEmail',
      }),
    })
  );

  const setRefetchToggleForStripeBillingEmail = useSetRecoilState(
    refetchToggle('legacy/getStripeBillingEmail')
  );

  /**
   * Retrive invoices from stripe charges
   */
  const [stripeInvoices, stripeLoadable] = makeApiRequestCached<
    Stripe.Response<Stripe.ApiList<Stripe.Charge>> | undefined
  >(
    {
      endpoint: 'legacy/getBusinessChargesFromStripe',
      payloadJsonString: JSON.stringify({
        call: 'getBusinessChargesFromStripe',
        page: stripeState.page,
        pageSize,
        newCursor: stripeState.cursor,
        created: generateCreatedObj(),
      }),
    },
    (loadable, cached) =>
      loadable.state === 'hasValue'
        ? {
            ...loadable.contents?.response?.data?.result?.[0]?.invoices,
            data: loadable.contents?.response?.data?.result?.[0]?.invoices?.data
              ?.filter((d) => d.invoice) // ensure invoice exists
              .map((row) => ({
                ...row,
                id: row.id,
              })),
          }
        : cached.current
  );

  const setRefetchToggleForStripeLoadable = useSetRecoilState(
    refetchToggle('legacy/getBusinessChargesFromStripe')
  );

  //get prorated charges
  const proratedChargesLoadable = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: 'legacy/getProratedChargesByBID',
      payloadJsonString: JSON.stringify({
        call: 'getProratedChargesByBID',
      }),
    })
  );

  const proratedChargesInvoiceIds: String[] = useMemo(() => {
    if (proratedChargesLoadable.contents?.response?.data?.result) {
      return proratedChargesLoadable.contents?.response?.data.result?.map(
        (thisCharge) => {
          return thisCharge.externalInvoiceid;
        }
      );
    } else {
      return [];
    }
  }, [proratedChargesLoadable]);

  const userIDsThatCanAccessRefundProratedChargeButton = [
    127136, //beaujean 995
    144182, //daniel lovig 995
    2117, //alex mann 995
    1619, //keith 995
    92226, //ben 995
    82495, //dg 995
    1599, //jeff k 995
    123188, //chris marshall 995
  ];

  const [_, businessDataLoadable] = makeApiRequestCached<
    IBusinessModel | undefined
  >(
    {
      endpoint: endpointEnum.GET_BUSINESS_DATA,
      payloadJsonString: JSON.stringify({
        call: methodNameEnum.GET_BUSINESS_DATA,
      }),
    },
    (loadable, cached) =>
      loadable.contents?.response?.data?.result?.[0] ?? cached.current
  );
  const businessUsersLoadable = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: endpointEnum.GET_BUSINESS_USERS,
      payloadJsonString: JSON.stringify({
        call: methodNameEnum.GET_BUSINESS_USERS,
      }),
    })
  );

  const handlePageChange = (page: number, details?: any) => {
    if (page > stripeState.page) {
      setStripeState((oldData) => {
        return {
          ...oldData,
          page: page,
          cursor: {
            starting_after:
              stripeInvoices.current?.data?.[
                stripeInvoices.current?.data?.length - 1
              ].id,
          },
        };
      });
    }
    if (page < stripeState.page) {
      setStripeState((oldData) => {
        return {
          ...oldData,
          page: page,
          cursor: {
            ending_before: stripeInvoices.current?.data?.[0].id,
          },
        };
      });
      newCursor.current = {
        ending_before: stripeInvoices.current?.data?.[0].id,
      };
    }
  };

  const handlePageSizeChange = (pageSize: number, details?: any) => {
    resetStripe();
    setPageSize(pageSize);
  };

  /**
   * Create cutom toolbar component for table
   * Use useCallback to stop rerendering
   */
  const ToolbarComponent = useCallback(
    (props: IToolbarComponent) => (
      <EnhancedTableToolbar
        {...props}
        hideSearch={true}
        debounceTimeout={500} // add 1/2 second debounce vs 100ms default value
        isLoading={stripeLoadable.state === 'loading'} // do not block on search
        dataType="Calls"
        leftComponent={
          <div
            style={{
              paddingLeft: '12px',
              width: '300px',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <p style={{ margin: 0 }}>
              <b>Billing Email:</b>{' '}
              {billingEmailLoadable.state === 'hasValue' &&
                billingEmailLoadable?.contents?.response?.data?.email}
            </p>
            <a
              href=""
              onClick={(e) => {
                e.preventDefault();
                setShowEditEmailModal(true);
              }}
            >
              Edit billing email
            </a>
          </div>
        }
      />
    ),
    [stripeLoadable, billingEmailLoadable, proratedChargesLoadable]
  );

  /**
   * Create column for DataGrid renderer
   * use useMemo to prevent unneeded rerenderings
   */
  const columns = useMemo<IColDef[]>(
    () => [
      {
        field: 'created',
        headerName: 'Date',
        headerAlign: 'left',
        align: 'center',
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
        enableSorting: false,
        valueFormatter: (params) =>
          moment.unix(params.row.invoice.created).format('MM/DD/YYYY'),
      },
      {
        field: 'amount_due',
        headerName: 'Amount',
        headerAlign: 'left',
        align: 'center',
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
        enableSorting: false,
        valueFormatter: (params) => `$${params.row.invoice.amount_due / 100}`,
      },
      {
        field: 'status',
        headerName: 'Status',
        headerAlign: 'left',
        align: 'center',
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
        enableSorting: false,
        renderCell: (params) => {
          if (params.row.refunded) {
            return (
              <div
                className=""
                style={{
                  backgroundColor: 'rgba(168, 168, 168, 0.5)',
                  width: '60%',
                  height: '47%',
                  display: 'flex',
                  justifyContent: 'center',
                  padding: '3px 5px 5px',
                }}
              >
                <p style={{ paddingRight: '3px', marginBottom: 0 }}>refunded</p>
                <ReplayIcon fontSize="small" />
              </div>
            );
          }

          switch (params.row.status) {
            case 'succeeded':
              return (
                <div
                  className=""
                  style={{
                    backgroundColor: 'rgba(66, 245, 75, 0.5)',
                    width: '70%',
                    height: '47%',
                    display: 'flex',
                    justifyContent: 'center',
                    padding: '3px 5px 5px',
                  }}
                >
                  <p style={{ paddingRight: '3px', marginBottom: 0 }}>
                    Succeeded
                  </p>
                  <CheckIcon fontSize="small" />
                </div>
              );
            case 'pending':
              return (
                <div
                  className=""
                  style={{
                    backgroundColor: 'rgba(168, 168, 168, 0.5)',
                    width: '60%',
                    height: '47%',
                    display: 'flex',
                    justifyContent: 'center',
                    padding: '3px 5px 5px',
                  }}
                >
                  <p style={{ paddingRight: '3px', marginBottom: 0 }}>
                    pending
                  </p>
                  <ArrowForwardIcon fontSize="small" />
                </div>
              );
            case 'failed':
              return (
                <div
                  className=""
                  style={{
                    backgroundColor: 'rgba(230, 0, 0, 0.6)',
                    width: '48%',
                    height: '47%',
                    display: 'flex',
                    justifyContent: 'center',
                    padding: '3px 5px 5px',
                  }}
                >
                  <p style={{ paddingRight: '3px', marginBottom: 0 }}>Failed</p>
                  <ClearIcon fontSize="small" />
                </div>
              );
            default:
              return <div></div>;
          }
        },
      },
      {
        field: 'id',
        headerName: 'Invoice ID',
        headerAlign: 'center',
        align: 'center',
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
        enableSorting: false,
        renderCell: (params) => {
          return (
            <a
              href={`${params.row.invoice.hosted_invoice_url}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {params.row.invoice.id}
            </a>
          );
        },
      },
      {
        field: 'invoice_actions',
        headerName: 'Actions',
        headerAlign: 'left',
        align: 'right',
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
        enableSorting: false,
        renderCell: (params) => (
          <div>
            <IconButton
              href={params.row.invoice.invoice_pdf}
              download
              size="large"
            >
              <GetAppIcon />
            </IconButton>
            {proratedChargesInvoiceIds.includes(params.row.invoice.id) &&
              role === Role.SUPERADMIN &&
              (process.env.NODE_ENV === 'production'
                ? userIDsThatCanAccessRefundProratedChargeButton.includes(
                    userAtom.userid
                  )
                : true) &&
              params.row.status === 'succeeded' &&
              !params.row.refunded && (
                <IconButton
                  className="superStyle"
                  onClick={(e) => {
                    e.preventDefault();
                    setRefundProratedChargeInvoiceId(params.row.invoice.id);
                    setRefundProratedChargeId(params.row.id);
                    setRefundProratedChargeAmount(
                      params.row.invoice.amount_due / 100
                    );
                    setShowRefundProratedChargeModal(true);
                  }}
                >
                  <CurrencyExchangeIcon />
                </IconButton>
              )}
          </div>
        ),
      },
    ],
    [
      businessUsersLoadable,
      role,
      callRecord,
      businessDataLoadable,
      proratedChargesLoadable,
    ]
  );

  const customPaginationActions = (props: TablePaginationActionsProps) => {
    const { page } = props;

    const handleLeftButtonClick = (event) => {
      handlePageChange?.(page, {
        pageCount: 0,
        pageSize: pageSize ?? pageSize,
        rowCount: 0,
        paginationMode: 'server',
      });
    };

    const handleRightButtonClick = (event) => {
      handlePageChange?.(page + 2, {
        pageCount: 0,
        pageSize: pageSize ?? pageSize,
        rowCount: 0,
        paginationMode: 'server',
      });
    };

    return (
      <Box /* todo replace Box with @mui/material*/ style={{ flexShrink: 0 }}>
        <IconButton
          onClick={(e) => {
            handleLeftButtonClick(e);
            if (stripeState.direction !== 'backwards') {
              setStripeState((oldData) => {
                return {
                  ...oldData,
                  direction: 'backwards',
                };
              });
            }

            if (stripeState.hasChangedDirections !== true) {
              setStripeState((oldData) => {
                return {
                  ...oldData,
                  hasChangedDirections: true,
                };
              });
            }
          }}
          disabled={page === 0 || stripeLoadable.state === 'loading'}
          aria-label="first page"
          size="large"
        >
          {<KeyboardArrowLeftIcon />}
        </IconButton>
        <IconButton
          onClick={(e) => {
            handleRightButtonClick(e);
            if (stripeState.direction !== 'forwards') {
              setStripeState((oldData) => {
                return {
                  ...oldData,
                  direction: 'forwards',
                };
              });
            }
          }}
          disabled={
            stripeLoadable.state === 'loading' ||
            (stripeState.direction === 'backwards' &&
              stripeState.page === 1 &&
              !stripeInvoices.current?.has_more &&
              !stripeState.hasChangedDirections) ||
            (stripeState.page === 1 &&
              stripeState.direction === 'forwards' &&
              !stripeInvoices.current?.has_more &&
              !stripeState.hasChangedDirections) ||
            (page > 0 &&
              stripeState.direction === 'forwards' &&
              !stripeInvoices.current?.has_more)
          }
          aria-label="previous page"
          size="large"
        >
          {<KeyboardArrowRightIcon />}
        </IconButton>
      </Box>
    );
  };

  return (
    <>
      <DataTable
        rows={stripeInvoices.current?.data ?? []}
        columns={columns}
        rowCount={-1}
        page={stripeState.page}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        paginationMode="server"
        sortingMode="server"
        sortModel={sortModel}
        hideSortIcon={true}
        loading={stripeLoadable.state === 'loading'}
        ToolbarComponent={ToolbarComponent}
        pagination
        customPaginationActions={customPaginationActions}
      />
      <EditBillingEmailModal
        setRefetchToggleForStripeBillingEmail={
          setRefetchToggleForStripeBillingEmail
        }
        show={showEditEmailModal}
        mutation={mutation}
        handleClose={() => setShowEditEmailModal(false)}
      />
      {role === Role.SUPERADMIN && (
        <RefundProratedChargeModal
          chargeid={refundProratedChargeId}
          invoiceid={refundProratedChargeInvoiceId}
          amount={refundProratedChargeAmount}
          businessid={thisBID}
          show={showRefundProratedChargeModal}
          mutation={mutation}
          handleClose={() => {
            setRefundProratedChargeId('');
            setRefundProratedChargeInvoiceId('');
            setRefundProratedChargeAmount(0);
            setShowRefundProratedChargeModal(false);
          }}
          setRefetchToggleForStripeLoadable={setRefetchToggleForStripeLoadable}
        />
      )}
    </>
  );
};
